/**
 * ZIP Code Lookup Service
 *
 * Service for querying ZIP codes within a drawn geometry using Supabase Edge Function
 */

import { supabase } from '@vehix/shared/admin/portal-bridge';
import type { GeoJSONGeometry } from '../utils/zoneGeometry';

export interface ZipCodeLookupResponse {
  zip_codes: string[];
}

export interface ZipCodeLookupError {
  error: string;
}

/**
 * Look up ZIP codes that intersect with a given geometry
 * Uses Census Bureau TIGERweb API via Supabase Edge Function (no database required)
 */
export async function lookupZipCodes(geometry: GeoJSONGeometry): Promise<string[]> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const projectRef = supabaseUrl?.match(/https?:\/\/([^.]+)\.supabase\.co/)?.[1];

    if (!projectRef) {
      throw new Error('Invalid Supabase URL configuration');
    }

    // Get session for authenticated request
    const {
      data: { session }
    } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Authentication required');
    }

    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/get-zips`;

    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || ''
      },
      body: JSON.stringify({ geometry })
    });

    if (!response.ok) {
      let errorData: ZipCodeLookupError | any;
      try {
        errorData = await response.json();
      } catch {
        const text = await response.text();
        errorData = { error: `HTTP ${response.status}: ${response.statusText}`, body: text };
      }

      throw new Error(
        errorData.error || errorData.details || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    const data: ZipCodeLookupResponse = await response.json();
    return data.zip_codes || [];
  } catch (error) {
    throw error instanceof Error ? error : new Error('Failed to lookup ZIP codes');
  }
}
